<template>
  <div v-if="showPanel" class="col-12 card-wrapper">
    <div class="card mb-4 controlled-correction">
      <div class="card-header controlled-correction__header">
        <div>
          <span class="controlled-correction__kicker">{{ panelKicker }}</span>
          <h3 class="mb-1">{{ panelTitle }}</h3>
          <p class="mb-0 text-muted">{{ panelDescription }}</p>
        </div>
        <span class="controlled-correction__badge">
          <i :class="hasLinkedFinancialDocument ? 'fas fa-lock' : 'fas fa-pen'" class="mr-1"></i>
          {{ panelBadge }}
        </span>
      </div>

      <div class="card-body">
        <div v-if="loading && !context" class="controlled-correction__loading">
          <i class="fas fa-spinner fa-spin mr-2"></i>Cargando alternativas y stock...
        </div>

        <div v-if="loadErrors.length" class="alert alert-danger" role="alert">
          <strong>No pudimos cargar la corrección controlada.</strong>
          <div v-for="(error, index) in loadErrors" :key="`load-${index}`">{{ error }}</div>
          <button type="button" class="btn btn-link btn-sm p-0 mt-2" @click="fetchContext()">Reintentar</button>
        </div>

        <template v-if="context">
          <div v-if="lockMessages.length" class="controlled-correction__notice" role="status">
            <i class="fas fa-file-invoice-dollar"></i>
            <div>
              <strong>Por qué está bloqueada la edición general</strong>
              <div v-for="(message, index) in lockMessages" :key="`blocker-${index}`">{{ message }}</div>
            </div>
          </div>

          <div v-if="!context.sameVarietyEligible" class="alert alert-warning mb-0" role="alert">
            <strong>Esta corrección no está disponible.</strong>
            <div v-for="(blocker, index) in context.sameVarietyBlockers || []" :key="`restricted-${index}`">
              {{ blocker.message }}
            </div>
          </div>

          <div v-else>
            <div v-for="item in context.items" :key="item.id" class="controlled-correction__item">
              <div class="controlled-correction__item-heading">
                <div>
                  <span class="controlled-correction__kicker">{{ item.varietyName || 'MISMA VARIEDAD' }}</span>
                  <h4 class="mb-0">{{ item.code }} · {{ item.description }}</h4>
                </div>
                <span class="controlled-correction__source-current"
                  >Origen actual: {{ sourceName(item.stockSource) }}</span
                >
              </div>

              <div v-if="hasLinkedFinancialDocument" class="row controlled-correction__readonly">
                <div class="col-md-4">
                  <small>Cantidad</small>
                  <strong>{{ item.quantity }} bolsas</strong>
                </div>
                <div class="col-md-4">
                  <small>Precio unitario</small>
                  <strong>{{ item.unitPrice | toCurrencyUSD }}</strong>
                </div>
                <div class="col-md-4">
                  <small>Costo distribuidor</small>
                  <strong>{{ item.distributorCost | toCurrencyUSD }}</strong>
                </div>
              </div>

              <div v-else class="row controlled-correction__values">
                <div class="col-md-4 controlled-correction__readonly">
                  <small>Cantidad</small>
                  <strong>{{ item.quantity }} bolsas</strong>
                </div>
                <div class="col-md-4">
                  <div class="form-group mb-0">
                    <label class="control-label">Precio unitario (USD)</label>
                    <el-input
                      v-model="draftFor(item).unitPrice"
                      type="number"
                      min="0"
                      step="0.01"
                      aria-label="Precio unitario en dólares"
                      :disabled="isSaving(item.id)"
                      @input="clearRowErrors(item.id)"
                    />
                    <small
                      v-if="financialValueError(draftFor(item).unitPrice, 'El precio unitario')"
                      class="text-danger"
                    >
                      {{ financialValueError(draftFor(item).unitPrice, 'El precio unitario') }}
                    </small>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="form-group mb-0">
                    <label class="control-label">Costo distribuidor (USD)</label>
                    <el-input
                      v-model="draftFor(item).distributorCost"
                      type="number"
                      min="0"
                      step="0.01"
                      aria-label="Costo del distribuidor en dólares"
                      :disabled="isSaving(item.id)"
                      @input="clearRowErrors(item.id)"
                    />
                    <small
                      v-if="financialValueError(draftFor(item).distributorCost, 'El costo del distribuidor')"
                      class="text-danger"
                    >
                      {{ financialValueError(draftFor(item).distributorCost, 'El costo del distribuidor') }}
                    </small>
                  </div>
                </div>
              </div>

              <div v-if="rowErrors[item.id] && rowErrors[item.id].length" class="alert alert-danger" role="alert">
                <strong>No se pudo guardar este renglón.</strong>
                <div v-for="(error, index) in rowErrors[item.id]" :key="`${item.id}-error-${index}`">
                  {{ error }}
                </div>
              </div>

              <div class="form-group">
                <label class="control-label d-block">Origen de stock</label>
                <div class="controlled-correction__sources" role="radiogroup" aria-label="Origen de stock">
                  <button
                    type="button"
                    class="controlled-correction__source"
                    :class="{ active: draftFor(item).stockSource === STOCK_SOURCE_CENTRAL }"
                    :aria-pressed="draftFor(item).stockSource === STOCK_SOURCE_CENTRAL"
                    :disabled="isSaving(item.id)"
                    @click="selectSource(item, STOCK_SOURCE_CENTRAL)"
                  >
                    <i class="fas fa-warehouse"></i>
                    <span><strong>Casa Central</strong><small>Disponibilidad general</small></span>
                  </button>
                  <button
                    type="button"
                    class="controlled-correction__source"
                    :class="{ active: draftFor(item).stockSource === STOCK_SOURCE_DISTRIBUTOR }"
                    :aria-pressed="draftFor(item).stockSource === STOCK_SOURCE_DISTRIBUTOR"
                    :disabled="isSaving(item.id)"
                    @click="selectSource(item, STOCK_SOURCE_DISTRIBUTOR)"
                  >
                    <i class="fas fa-boxes"></i>
                    <span>
                      <strong>MiDepósito</strong>
                      <small>{{ distributorName }}</small>
                    </span>
                  </button>
                </div>
              </div>

              <div class="row">
                <div class="col-lg-7">
                  <div class="form-group">
                    <label class="control-label">Código de la misma variedad</label>
                    <el-select
                      v-model="draftFor(item).articleId"
                      class="w-100"
                      filterable
                      :disabled="isSaving(item.id)"
                      placeholder="Seleccioná un código con stock"
                      @change="clearRowErrors(item.id)"
                    >
                      <el-option
                        v-for="article in articleOptionsFor(item)"
                        :key="article.id"
                        :label="`${article.code} · ${article.description}`"
                        :value="article.id"
                        :disabled="!isArticleSelectable(item, article)"
                      >
                        <div
                          class="controlled-correction__article-option"
                          :class="{ 'is-unavailable': !isArticleSelectable(item, article) }"
                        >
                          <span>{{ article.code }} · {{ article.description }}</span>
                          <strong>{{ stockFor(article, draftFor(item).stockSource) }} bolsas</strong>
                        </div>
                      </el-option>
                    </el-select>
                    <small v-if="selectableArticlesFor(item).length === 0" class="text-danger d-block mt-1">
                      No hay códigos de esta variedad con {{ item.quantity }} bolsas disponibles en
                      {{ sourceName(draftFor(item).stockSource) }}.
                    </small>
                    <div
                      v-else-if="selectedArticleFor(item) && !isSelectedArticleAvailable(item)"
                      class="controlled-correction__stock-warning mt-2"
                      role="alert"
                    >
                      <i class="fas fa-exclamation-triangle"></i>
                      <span>
                        <strong>{{ selectedArticleFor(item).code }}</strong> tiene
                        {{ stockFor(selectedArticleFor(item), draftFor(item).stockSource) }} bolsas en
                        {{ sourceName(draftFor(item).stockSource) }} y el renglón necesita {{ item.quantity }}. Elegí
                        otro código de la misma variedad.
                      </span>
                    </div>
                    <small v-else-if="selectedArticleFor(item)" class="text-muted d-block mt-1">
                      Stock en {{ sourceName(draftFor(item).stockSource) }}:
                      {{ stockFor(selectedArticleFor(item), draftFor(item).stockSource) }} bolsas.
                    </small>
                  </div>
                </div>

                <div class="col-lg-5">
                  <div class="form-group">
                    <label class="control-label">Motivo del cambio (*)</label>
                    <el-input
                      v-model="draftFor(item).reason"
                      :disabled="isSaving(item.id)"
                      :maxlength="220"
                      placeholder="Explicá por qué se modifica este renglón"
                      type="textarea"
                      :autosize="{ minRows: 2 }"
                      @input="clearRowErrors(item.id)"
                    />
                    <small class="text-muted">Entre 5 y 220 caracteres.</small>
                  </div>
                </div>
              </div>

              <div class="text-right">
                <base-button
                  native-type="button"
                  type="primary"
                  :loading="isSaving(item.id)"
                  :disabled="!canSave(item)"
                  @click="saveItem(item)"
                >
                  {{ isSaving(item.id) ? 'Guardando' : saveButtonLabel }}
                </base-button>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { Select, Option, Input } from 'element-ui';

import { STOCK_SOURCE_CENTRAL, STOCK_SOURCE_DISTRIBUTOR } from '@/util/constants/orders';
import {
  availabilityFor,
  buildSameVarietyPayload,
  correctionModeForContext,
  eligibleArticlesFor,
  hasControlledCorrectionItems,
  hasFinancialCorrectionBlocker,
  hasSameVarietyDraftChanges,
  isArticleAvailableFor,
  isValidFinancialValue,
  normalizeControlledCorrectionErrors,
  sameVarietyArticlesFor,
  sourceLabel,
} from '@/util/orderControlledCorrection';

export default {
  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    [Input.name]: Input,
  },

  props: {
    orderId: {
      type: Number,
      required: true,
    },

    orderState: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      context: null,
      drafts: {},
      rowErrors: {},
      savingItemIds: [],
      loadErrors: [],
      loading: false,
      STOCK_SOURCE_CENTRAL,
      STOCK_SOURCE_DISTRIBUTOR,
    };
  },

  computed: {
    hasLinkedFinancialDocument() {
      return hasFinancialCorrectionBlocker(this.context);
    },

    showPanel() {
      return (
        Boolean(this.context && this.context.sameVarietyEligible && hasControlledCorrectionItems(this.context)) ||
        this.hasLinkedFinancialDocument ||
        (['open', 'accepted'].includes(this.orderState) && this.loadErrors.length > 0)
      );
    },

    lockMessages() {
      if (!this.context) return [];

      return (this.context.blockers || [])
        .filter((blocker) => ['receipt', 'liquidation'].includes(blocker.code))
        .map((blocker) => blocker.message);
    },

    distributorName() {
      return this.context && this.context.distributor ? this.context.distributor.name : 'Distribuidor asignado';
    },

    panelKicker() {
      return this.hasLinkedFinancialDocument ? 'CORRECCIÓN CONTROLADA' : 'EDICIÓN CONTROLADA';
    },

    panelTitle() {
      return this.hasLinkedFinancialDocument
        ? 'Corregir origen o código del artículo'
        : 'Editar origen, código y valores';
    },

    panelDescription() {
      return this.hasLinkedFinancialDocument
        ? 'El comprobante conserva bloqueada la edición general. Acá sólo podés corregir el origen y elegir otro código de la misma variedad; cantidad, precio y costo no cambian.'
        : 'Podés cambiar el origen y el código dentro de la misma variedad, además de editar el precio y el costo. Todos los cambios se guardan juntos.';
    },

    panelBadge() {
      return this.hasLinkedFinancialDocument ? 'Valores protegidos' : 'Valores editables';
    },

    saveButtonLabel() {
      return this.hasLinkedFinancialDocument ? 'Guardar corrección' : 'Guardar cambios';
    },
  },

  mounted() {
    this.fetchContext();
  },

  methods: {
    async fetchContext(announceLoading = true) {
      this.loading = true;
      this.loadErrors = [];
      if (announceLoading) {
        this.$emit('correction-mode', 'loading');
      }

      try {
        const response = await this.$services.resource('orders').controlledCorrection(this.orderId, 'same_variety');
        this.context = response.data.data;
        this.hydrateDrafts(this.context.items || []);
        this.$emit('correction-mode', correctionModeForContext(this.context));
      } catch (errors) {
        this.loadErrors = normalizeControlledCorrectionErrors(errors);
        this.$emit('correction-mode', 'error');
      } finally {
        this.loading = false;
      }
    },

    hydrateDrafts(items) {
      const drafts = {};
      items.forEach((item) => {
        drafts[item.id] = {
          articleId: item.articleId,
          stockSource: item.stockSource,
          unitPrice: item.unitPrice,
          distributorCost: item.distributorCost,
          reason: '',
        };
      });
      this.drafts = drafts;
      this.rowErrors = {};
    },

    draftFor(item) {
      if (!this.drafts[item.id]) {
        this.$set(this.drafts, item.id, {
          articleId: item.articleId,
          stockSource: item.stockSource,
          unitPrice: item.unitPrice,
          distributorCost: item.distributorCost,
          reason: '',
        });
      }

      return this.drafts[item.id];
    },

    sourceName(stockSource) {
      return sourceLabel(stockSource);
    },

    articleOptionsFor(item) {
      const sourceArticles =
        item.eligibleArticles && item.eligibleArticles.length ? item.eligibleArticles : this.context.articles || [];

      return sameVarietyArticlesFor(item, sourceArticles);
    },

    selectableArticlesFor(item) {
      return eligibleArticlesFor(item, this.articleOptionsFor(item), this.draftFor(item).stockSource);
    },

    selectedArticleFor(item) {
      const articleId = this.draftFor(item).articleId;
      return this.articleOptionsFor(item).find((article) => Number(article.id) === Number(articleId));
    },

    isArticleSelectable(item, article) {
      return isArticleAvailableFor(item, article, this.draftFor(item).stockSource);
    },

    isSelectedArticleAvailable(item) {
      return this.isArticleSelectable(item, this.selectedArticleFor(item));
    },

    stockFor(article, stockSource) {
      return availabilityFor(article, stockSource);
    },

    selectSource(item, stockSource) {
      const draft = this.draftFor(item);
      draft.stockSource = stockSource;
      this.clearRowErrors(item.id);
    },

    clearRowErrors(itemId) {
      this.$delete(this.rowErrors, itemId);
    },

    isSaving(itemId) {
      return this.savingItemIds.includes(itemId);
    },

    financialValueError(value, label) {
      return isValidFinancialValue(value) ? '' : `${label} debe ser un número mayor o igual a cero.`;
    },

    canSave(item) {
      const draft = this.draftFor(item);
      const reason = (draft.reason || '').trim();
      const financialValuesEditable = !this.hasLinkedFinancialDocument;
      const financialValuesValid =
        !financialValuesEditable ||
        (isValidFinancialValue(draft.unitPrice) && isValidFinancialValue(draft.distributorCost));
      const changed = hasSameVarietyDraftChanges(item, draft, financialValuesEditable);

      return Boolean(
        changed &&
          financialValuesValid &&
          draft.articleId &&
          this.selectedArticleFor(item) &&
          this.isSelectedArticleAvailable(item) &&
          reason.length >= 5 &&
          reason.length <= 220 &&
          !this.isSaving(item.id)
      );
    },

    async saveItem(item) {
      if (!this.canSave(item)) return;

      const draft = this.draftFor(item);
      this.clearRowErrors(item.id);
      this.savingItemIds.push(item.id);

      try {
        await this.$services
          .resource('orders')
          .correctSameVariety(
            this.orderId,
            buildSameVarietyPayload(this.context.orderUpdatedAt, item, draft, !this.hasLinkedFinancialDocument)
          );

        this.$success(
          this.hasLinkedFinancialDocument ? 'Corrección guardada' : 'Renglón actualizado',
          this.hasLinkedFinancialDocument
            ? 'El pedido conserva sus cantidades y valores.'
            : 'Los cambios del renglón quedaron guardados.'
        );
        await this.fetchContext(false);
        this.$emit('corrected');
      } catch (errors) {
        this.$set(this.rowErrors, item.id, normalizeControlledCorrectionErrors(errors));
      } finally {
        this.savingItemIds = this.savingItemIds.filter((id) => id !== item.id);
      }
    },
  },
};
</script>

<style scoped>
.controlled-correction {
  border-top: 4px solid #f4c430;
}

.controlled-correction__header,
.controlled-correction__item-heading,
.controlled-correction__source,
.controlled-correction__notice,
.controlled-correction__article-option {
  display: flex;
  align-items: center;
}

.controlled-correction__header,
.controlled-correction__item-heading,
.controlled-correction__article-option {
  justify-content: space-between;
  gap: 1rem;
}

.controlled-correction__article-option.is-unavailable {
  color: #9aa4b2;
}

.controlled-correction__stock-warning {
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  padding: 0.65rem 0.75rem;
  border-left: 3px solid #fb6340;
  border-radius: 0.3rem;
  color: #7a341f;
  font-size: 0.82rem;
  line-height: 1.4;
  background: #fff3ee;
}

.controlled-correction__header {
  align-items: flex-start;
}

.controlled-correction__kicker {
  display: block;
  margin-bottom: 0.2rem;
  color: #6b7280;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.12em;
}

.controlled-correction__badge,
.controlled-correction__source-current {
  border: 1px solid #f4c430;
  border-radius: 999px;
  color: #172b4d;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.4rem 0.65rem;
  white-space: nowrap;
  background: #fff8d6;
}

.controlled-correction__loading {
  padding: 1rem;
  color: #536273;
  text-align: center;
}

.controlled-correction__notice {
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 1rem;
  padding: 0.85rem 1rem;
  border-left: 4px solid #f4c430;
  border-radius: 0.35rem;
  color: #5f4708;
  background: #fff8e1;
}

.controlled-correction__item {
  padding: 1.1rem;
  border: 1px solid #d8dee8;
  border-radius: 0.5rem;
  background: #f8fafc;
}

.controlled-correction__item + .controlled-correction__item {
  margin-top: 1rem;
}

.controlled-correction__readonly {
  margin: 1rem 0;
}

.controlled-correction__values {
  align-items: flex-start;
  margin: 1rem 0;
}

.controlled-correction__values .controlled-correction__readonly {
  margin: 0;
  padding-top: 0.55rem;
}

.controlled-correction__values > div {
  margin-bottom: 0.5rem;
}

.controlled-correction__readonly > div {
  margin-bottom: 0.5rem;
}

.controlled-correction__readonly small,
.controlled-correction__readonly strong {
  display: block;
}

.controlled-correction__readonly small {
  color: #8898aa;
  font-size: 0.72rem;
  text-transform: uppercase;
}

.controlled-correction__sources {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.controlled-correction__source {
  gap: 0.75rem;
  padding: 0.75rem 0.9rem;
  border: 1px solid #cfd6e1;
  border-radius: 0.4rem;
  color: #344050;
  text-align: left;
  background: #fff;
}

.controlled-correction__source.active {
  border-color: #172b4d;
  box-shadow: inset 0 0 0 1px #172b4d;
}

.controlled-correction__source i {
  color: #172b4d;
}

.controlled-correction__source strong,
.controlled-correction__source small {
  display: block;
}

.controlled-correction__source small {
  color: #6b7280;
}

.controlled-correction__article-option strong {
  flex: 0 0 auto;
  color: #172b4d;
}

@media (max-width: 767px) {
  .controlled-correction__header,
  .controlled-correction__item-heading {
    display: block;
  }

  .controlled-correction__badge,
  .controlled-correction__source-current {
    display: inline-block;
    margin-top: 0.75rem;
  }

  .controlled-correction__sources {
    grid-template-columns: 1fr;
  }
}
</style>
